"use client";

import { useSyncExternalStore } from "react";
import { announceWorkspacePageCreation, workspaceNavigationVersion } from "./workspace-navigation-shell";
import { cancelWorkspacePageWarmup } from "@/lib/api/workspace-page-warmup";
import { showToast } from "@/app/components/Toast";
import { preloadWorkspacePageCode } from "./workspace-page-code-loader";
import type { WorkspaceNavigationLabel } from "./workspace-navigation-shell";

export const WORKSPACE_PAGES_UPDATED = "frogstat:workspace-pages-updated";
type CreatedPageNavigate = (href: string, page: WorkspaceNavigationLabel) => void;

/** Only metadata returned by the successful creation POST, never link text. */
function createdPageLabel(raw: unknown): WorkspaceNavigationLabel {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) throw new Error("Не получилось создать страницу");
  const page = raw as Record<string, unknown>;
  if (typeof page.id !== "string" || !page.id || page.id.length > 128 || typeof page.title !== "string") {
    throw new Error("Не получилось создать страницу");
  }
  return {
    id: page.id, title: page.title, icon: typeof page.icon === "string" ? page.icon : null,
    ...(page.description === null || typeof page.description === "string" ? { description: page.description } : {}),
    ...(typeof page.updated_at === "string" && Number.isFinite(Date.parse(page.updated_at)) ? { updated_at: page.updated_at } : {}),
    ...(page.cover === null || typeof page.cover === "string" ? { hasCover: !!page.cover } : {}),
    // Do not guess from the requested shop: the server may have rejected it.
    ...(page.store_ref === null || typeof page.store_ref === "string" ? { store_ref: page.store_ref } : {}),
  };
}

/** One create request per tab, even when sidebar/header buttons are clicked
 * together. Leaving while it runs must not redirect back when it completes. */
export function createWorkspacePageCreator(deps: {
  fetcher: typeof fetch;
  start: () => void;
  version: () => number;
  navigate: (href: string) => void;
  created?: () => void;
  error: (message: string) => void;
}) {
  let pending: Promise<void> | null = null;
  const listeners = new Set<() => void>();
  const emit = () => { for (const listener of listeners) listener(); };
  return {
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    isPending: () => pending !== null,
    create(title?: string, storeRef?: string, navigate?: CreatedPageNavigate, folderId?: string): Promise<void> {
      if (pending) return pending;
      // Reserve before notifying listeners or starting navigation (both can
      // synchronously trigger another button). The POST starts this microtask.
      pending = Promise.resolve().then(async () => {
        deps.start();
        const version = deps.version();
        try {
          const res = await deps.fetcher("/api/workspace-pages", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...(title ? { title } : {}), ...(storeRef ? { storeRef } : {}), ...(folderId ? { folderId } : {}) }),
          });
          if (!res.ok) throw new Error(res.status === 403 ? "В демо-режиме создание отключено" : "Не получилось создать страницу");
          const page = createdPageLabel(await res.json());
          deps.created?.();
          if (version === deps.version()) {
            const href = `/page/${encodeURIComponent(page.id)}`;
            if (navigate) navigate(href, page);
            else deps.navigate(href);
          }
        } catch (error) {
          if (version === deps.version()) deps.error(error instanceof Error ? error.message : "Не получилось создать страницу");
        }
      }).finally(() => { pending = null; emit(); });
      emit();
      return pending;
    },
  };
}

const creator = createWorkspacePageCreator({
  fetcher: (...args) => fetch(...args),
  start: () => { cancelWorkspacePageWarmup(); announceWorkspacePageCreation(); preloadWorkspacePageCode(); },
  version: workspaceNavigationVersion,
  navigate: href => window.location.assign(href),
  created: () => window.dispatchEvent(new Event(WORKSPACE_PAGES_UPDATED)),
  error: message => showToast(message, "error"),
});
export const createPage = creator.create;
const notPending = () => false;
export function useWorkspacePageCreating(): boolean {
  return useSyncExternalStore(creator.subscribe, creator.isPending, notPending);
}
